<template>
  <div>
    <b-form-group>
      <div class="text-lg-center">
        <h5>
          {{ $t('diagnosticConfigIntro') }}
        </h5>
      </div>
    </b-form-group>
    <b-form-group>
      <b-form-row
        v-for="(input, index) in diagnosticRows"
        :key="input.uuid">
        <b-col lg="2">
          <b-form-group :label="index === 0 ? $t('formKit') : undefined">
            <app-kit-editable-multiselect
              v-model="input.kit"
              label="name"
              :overriding-options="currentKits"
              :disabled="input.id !== null"
              @input="resetReactionBuffers(input)" />
          </b-form-group>
        </b-col>
        <b-col lg="2">
          <b-form-group :label="index === 0 ? $t('formReactionBuffer') : undefined">
            <app-reactionBuffer-dropdown
              v-model="input.reactionBuffer"
              label="name"
              :overriding-options="input.kit?.reactionBuffers"
              :disabled="input.id !== null"
              @input="resetChannels(input)" />
          </b-form-group>
        </b-col>
        <b-col lg="2">
          <b-form-group :label="index === 0 ? $t('formChannel') : undefined">
            <app-channel-dropdown
              v-model="input.channel"
              label="name"
              :overriding-options="input.reactionBuffer?.channels"
              :disabled="input.id !== null"
              @input="resetDiagnostics(input)" />
          </b-form-group>
        </b-col>
        <b-col lg="2">
          <b-form-group :label="index === 0 ? $t('formDiagnosticHeader') : undefined">
            <app-diagnostic-dropdown
              ref="diagnosticDropdown"
              v-model="input.diagnosticColor"
              label="name"
              :translated="true"
              :overriding-options="input.diagnosticColors"
              :disable="input.id !== null" />
          </b-form-group>
        </b-col>
        <b-col lg="3">
          <b-form-group :label="index === 0 ? $t('label') : undefined">
            <b-input v-model="input.label" />
          </b-form-group>
        </b-col>
        <b-col>
          <b-form-group
            :label="index === 0 ? 'Remove / Add' : undefined">
            <b-button
              ref="removeButton"
              :disabled="diagnosticRows.length === 0"
              variant="outline"
              class="btn--no-outline btn-icon btn-icon-minus"
              @click="removeDiagnosticInputLine(input)">
              <font-awesome-icon
                :icon="['fas', 'minus-circle']" />
            </b-button>
            <b-button
              v-if="index === diagnosticRows.length - 1"
              ref="addButton"
              variant="outline"
              class="btn--no-outline btn-icon btn-icon-plus"
              @click="addDiagnosticInputLine">
              <font-awesome-icon
                :icon="['fas', 'plus-circle']" />
            </b-button>
          </b-form-group>
        </b-col>
      </b-form-row>
      <b-form-row
        class="form-button">
        <b-button
          ref="saveButton"
          @click="save">
          {{ $t('save') }}
        </b-button>
      </b-form-row>
    </b-form-group>
  </div>
</template>

<script>

import { findAllKitsWithChannels, getDiagnosticColorsByChannel, getDiagnosticLabels, updateDiagnostic } from '@/service/GenefoxService';
import NotificationMixin from '@/mixins/NotificationMixin';
import KitEditableMultiselect from '../../common/KitEditableMultiselect';
import ReactionBufferDropdown from '../../common/ReactionBufferDropdown';
import DiagnosticDropdown from '../../common/DiagnosticDropdown';
import { v4 as uuidv4 } from 'uuid';
import cloneDeep from 'lodash/cloneDeep';
import ChannelDropdown from '@/components/common/ChannelDropdown.vue';

export default {
  name: 'ConfigurationFormDiagnostic',
  components: {
    'app-diagnostic-dropdown': DiagnosticDropdown,
    'app-kit-editable-multiselect': KitEditableMultiselect,
    'app-reactionBuffer-dropdown': ReactionBufferDropdown,
    'app-channel-dropdown': ChannelDropdown
  },
  mixins: [ NotificationMixin ],
  data() {
    return {
      diagnosticRows: [ {
        uuid: uuidv4(),
        id: null,
        kit: null,
        diagnosticColors: [],
        reactionBuffer: null,
        channel: null,
        label: null,
        diagnosticColor: null
      } ],
      currentKits: [],
      diagnosticLabels: []
    };
  },
  beforeMount() {
    findAllKitsWithChannels(true, false).then(kits => {
      this.currentKits = kits;
      getDiagnosticLabels()
        .then(diagnosticLabels => {
          this.diagnosticLabels = diagnosticLabels;
          this.diagnosticRows = this.buildDiagnosticRows(diagnosticLabels);
        });
    });
  },
  methods: {
    addDiagnosticInputLine() {
      this.diagnosticRows.push({
        uuid: uuidv4(),
        id: null,
        kit: null,
        diagnosticColors: [],
        reactionBuffer: null,
        label: null,
        diagnosticColor: null
      });
    },
    setDiagnosticColors(input) {
      if (input?.channel?.id) {
        getDiagnosticColorsByChannel(input.channel.id)
          .then(diagnosticsColors => {
            input.diagnosticColors = diagnosticsColors;
          })
          .catch(error => {
            console.error('Error loading diagnostic colors:', error);
            input.diagnosticColors = [];
          });
      }
    },
    resetReactionBuffers(input) {
      input.reactionBuffer = input.kit?.reactionBuffers?.length == 1 ? input.kit.reactionBuffers[0] : null;
      this.resetChannels(input);
    },
    resetChannels(input) {
      input.channel = input.reactionBuffer?.channels?.length == 1 ? input.reactionBuffer.channels[0] : null;
      this.resetDiagnostics(input);
    },
    resetDiagnostics(input) {
      input.label = null;
      input.diagnosticColor = null;
      input.diagnosticColors = [];

      this.$nextTick(() => {
        this.setDiagnosticColors(input);
      });
    },
    removeDiagnosticInputLine({ uuid }) {
      const index = this.diagnosticRows.findIndex(row => row.uuid === uuid);
      this.diagnosticRows.splice(index, 1);
      if (this.diagnosticRows.length === 0) {
        this.addDiagnosticInputLine();
      }
    },
    save() {
      let notComplete;
      if (!this.areDiagnosticsUnique()) {
        this.showErrorNotification(this.$t('infoTitleCannotSaveWithNoReactionBufferToAddOrDelete'), this.$t('errorDuplicateLabels'));

        return;
      }
      this.diagnosticRows.forEach(row => {
        if (!row.diagnosticColor || !row.label || !row.channel) {
          notComplete = true;
        }
      });
      if (notComplete) {
        if (!this.diagnosticRows[0].diagnosticColor
          && !this.diagnosticRows[0].label
          && !this.diagnosticRows[0].channel) {
          this.update(true);
        } else {
          this.showInfoNotification(this.$t('infoTitleCannotSaveWithNoReactionBufferToAddOrDelete'), this.$t('errorCannotSaveEmptyDiagnostic'));
        }
      } else {
        this.update(false);
      }
    },
    update(clear) {
      const self = this;
      const diagList = cloneDeep(this.diagnosticRows);
      if (clear) {
        diagList.splice(0, 1);
      }
      updateDiagnostic(diagList)
        .then(diagnosticsLabels => {
          self.diagnosticRows = this.buildDiagnosticRows(diagnosticsLabels);
          if (this.diagnosticRows.length === 0) {
            this.addDiagnosticInputLine();
          }
          this.showSuccessNotification(this.$t('formConfigureDiagnostic'), this.$t('successTitleConfigureDiagnostic'));
        })
        .catch(error => {
          this.showErrorNotification(this.$t('errorConfigureDiagnostic'), error);
        });
    },
    areDiagnosticsUnique() {
      const map = new Map();
      this.diagnosticRows.forEach(label => {
        const diagnosticName = label?.diagnosticColor?.diagnosticLabel === null ? label?.diagnosticColor?.diagnostic?.name : label?.diagnosticColor?.diagnostic?.name + label?.diagnosticColor?.diagnosticLabel;
        if (diagnosticName) {
          if (!map.has(diagnosticName)) {
            map.set(diagnosticName, new Map());
          }
          if (!map.get(diagnosticName).has(label.reactionBuffer.name)) {
            map.get(diagnosticName).set(label.reactionBuffer.name, []);
          }
          map.get(diagnosticName).get(label.reactionBuffer.name).push(label.channel.name);
        }
      });

      let isUnique = true;
      map.forEach(reactionBuffers => {
        reactionBuffers.forEach(channels => {
          const channelNames = new Set();
          for (const channel of channels) {
            if (channelNames.has(channel)) {
              isUnique = false;
            }
            channelNames.add(channel);
          }
        });
      });

      return isUnique;
    },
    buildDiagnosticRows(diagnosticsLabels) {
      const diagnosticLabelArray = [];
      if (diagnosticsLabels && diagnosticsLabels.length > 0) {
        diagnosticsLabels.forEach(diagnosticLabel => {
          const row = {
            uuid: uuidv4(),
            id: diagnosticLabel.id,
            kit: diagnosticLabel.kit,
            diagnosticColors: [],
            reactionBuffer: diagnosticLabel.reactionBuffer,
            channel: diagnosticLabel.channel,
            label: diagnosticLabel.label,
            diagnosticColor: diagnosticLabel.diagnosticColor
          };
          this.setDiagnosticColors(row);
          diagnosticLabelArray.push(row);
        });
      } else {
        diagnosticLabelArray.push({
          uuid: uuidv4(),
          id: null,
          kit: null,
          diagnosticColors: [],
          reactionBuffer: null,
          channel: null,
          label: null,
          diagnosticColor: null
        });
      }

      return diagnosticLabelArray;
    }
  }
};
</script>